//Typing
import React, { useState, useEffect, useCallback, memo } from 'react'
import Footer from '../components/Footer'
import BackButton from '../components/BackButton'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { FileText, CheckCircle2, Printer, X, User, ClipboardList, Phone, Calendar, Download, Home, Sparkle } from 'lucide-react'
import { getFormFields } from '../ServiceAPI/form_field/api'
import { getvehicle } from '../ServiceAPI/master/api'
import { isTokenUsable } from '../utils/authSession'
import {
  submitApplication,
  getApplicationDetails,
  reuploadApplicationDocument
} from '../ServiceAPI/application/api'

/**
 * LICENSE INPUT COMPONENT (MEMOIZED)
 */
const LicenseNumberInput = memo(
  ({ fieldId, licenseValue, licenseError, onLicenseChange }) => {
    const licenseKey = `checkbox_${fieldId}_license_number`

    return (
      <div className='mt-6 p-6 premium-card animate-fadeInScale'>
        <label
          htmlFor={licenseKey}
          className='premium-label'
        >
          License Number
          <span className='text-red-500 ml-1'>*</span>
        </label>

        <input
          id={licenseKey}
          name={licenseKey}
          type='text'
          value={licenseValue}
          onChange={onLicenseChange}
          autoComplete='off'
          placeholder='Enter your existing license number'
          className='premium-input'
        />

        {licenseError && (
          <span className='block text-red-500 text-sm mt-2 animate-pulse'>
            {licenseError}
          </span>
        )}
      </div>
    )
  },
  (prev, next) =>
    prev.fieldId === next.fieldId &&
    prev.licenseValue === next.licenseValue &&
    prev.licenseError === next.licenseError
)

LicenseNumberInput.displayName = 'LicenseNumberInput'

/**
 * DYNAMIC FORM FIELD (MEMOIZED)
 */
const DynamicFormField = memo(
  ({
    field,
    index,
    fieldValue,
    fieldError,
    licenseValue,
    licenseError,
    handleChange,
    handleLicenseChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileChange,
    uploadedFiles,
    vehicleOptions,
    renderFieldOptions,
    id,
    setUploadedFiles,
    setFieldErrors,
    fieldErrors
  }) => {
    const isRequired = field.Is_required === 1
    const serviceId1 = id === '1' || parseInt(id) === 1

    const commonProps = {
      id: field.Field_label,
      name: field.Field_label,
      onChange: handleChange,
      required: isRequired,
    }

    if (field.Input_type !== 'file') {
      commonProps.value = fieldValue ?? ''
    }

    /**
     * RENDER BY INPUT TYPE
     */
    const renderByType = () => {
      switch (field.Input_type) {

        /* ===========================
         * SELECT FIELD
         * =========================== */
        case 'select':
          return (
            <div className="relative">
              <select
                {...commonProps}
                className={`premium-input pr-10 appearance-none
                ${fieldError ? 'border-red-500 ring-4 ring-red-500/10' : ''}`}
              >
                <option value=''>Select {field.Field_label}</option>
                {renderFieldOptions(field)}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )

        /* ===========================
         * MULTILINE TEXTAREA
         * =========================== */
        case 'textarea':
          return (
            <textarea
              {...commonProps}
              rows='4'
              className={`premium-input resize-none
              ${fieldError ? 'border-red-500 ring-4 ring-red-500/10' : ''}`}
              placeholder={`Enter ${field.Field_label.toLowerCase()}`}
            />
          )

        /* ===========================
         * FILE UPLOAD WITH DRAG/DROP
         * =========================== */
        case 'file':
          return (
            <div className='relative w-full group'>
              <input
                id={field.Field_label}
                name={field.Field_label}
                type='file'
                accept='.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx'
                onChange={e => handleFileChange(e, field)}
                className='absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10'
              />

              <div
                className={`min-h-[120px] border-2 border-dashed rounded-2xl p-6
                flex items-center justify-center transition-all duration-300
                ${uploadedFiles[field.Field_label]
                    ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20'
                    : 'border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 group-hover:border-blue-500 group-hover:bg-blue-50/50 dark:group-hover:bg-blue-950/20'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, field)}
              >
                {uploadedFiles[field.Field_label] ? (
                  <div className='flex flex-col items-center space-y-3 w-full animate-fadeInScale'>
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center text-green-600">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className='text-sm font-medium text-gray-900 dark:text-gray-100 text-center max-w-xs truncate'>
                      {uploadedFiles[field.Field_label].name}
                    </span>

                    <button
                      type='button'
                      className='text-red-500 hover:text-red-600 text-sm font-semibold transition-colors'
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedFiles(prev => ({
                          ...prev,
                          [field.Field_label]: null
                        }))
                        setFieldErrors(prev => ({
                          ...prev,
                          [field.Field_label]: null
                        }))
                      }}
                    >
                      Remove File
                    </button>
                  </div>
                ) : (
                  <div className='text-center space-y-3'>
                    <div className='w-12 h-12 mx-auto bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 animate-float'>
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <div className='space-y-1'>
                      <span className='font-bold text-gray-700 dark:text-gray-200'>Click to upload</span>
                      <p className='text-xs text-gray-500 dark:text-gray-400'>
                        or drag and drop your files here
                      </p>
                      <small className='text-[10px] text-gray-400 block pt-2'>
                        JPG, PNG, PDF, DOC (Max 5MB)
                      </small>
                    </div>
                  </div>
                )}
              </div>

              {fieldErrors[field.Field_label] && (
                <span className='block text-red-500 text-sm mt-2 animate-pulse'>
                  {fieldErrors[field.Field_label]}
                </span>
              )}
            </div>
          )

        /* ===========================
         * CHECKBOX + LICENSE INPUT
         * =========================== */
        case 'checkbox':
          return (
            <div className="pt-2">
              <label className='flex items-center gap-4 cursor-pointer group'>
                <div className="relative">
                  <input
                    {...commonProps}
                    type='checkbox'
                    checked={!!fieldValue}
                    className='sr-only'
                  />
                  <div className={`w-6 h-6 border-2 rounded-md transition-all duration-300 flex items-center justify-center
                    ${fieldValue ? 'bg-blue-600 border-blue-600' : 'border-gray-300 group-hover:border-blue-400'}`}>
                    {fieldValue && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className='text-gray-700 dark:text-gray-200 font-medium select-none'>
                  {field.Field_label}
                  {isRequired && <span className='text-red-500 ml-1'>*</span>}
                </span>
              </label>

              {serviceId1 && !!fieldValue && (
                <LicenseNumberInput
                  fieldId={field.Id}
                  licenseValue={licenseValue}
                  licenseError={licenseError}
                  onLicenseChange={handleLicenseChange}
                />
              )}
            </div>
          )

        /* ===========================
         * EMAIL FIELD
         * =========================== */
        case 'email':
          return (
            <input
              {...commonProps}
              type='email'
              autoComplete='email'
              placeholder={`Enter ${field.Field_label.toLowerCase()}`}
              className={`premium-input
              ${fieldError ? 'border-red-500 ring-4 ring-red-500/10' : ''}`}
            />
          )

        /* ===========================
         * NUMBER FIELD
         * =========================== */
        case 'number':
          return (
            <input
              {...commonProps}
              type='number'
              placeholder={`Enter ${field.Field_label.toLowerCase()}`}
              className={`premium-input no-spinner
              ${fieldError ? 'border-red-500 ring-4 ring-red-500/10' : ''}`}
            />
          )

        /* ===========================
         * DATE PICKER
         * =========================== */
        case 'date':
          return (
            <div className="relative">
              <input
                {...commonProps}
                type='text'
                placeholder='DD-MM-YYYY'
                maxLength={10}
                onChange={(e) => {
                  let val = e.target.value.replace(/\D/g, '');
                  let formatted = val;
                  if (val.length > 4) {
                    formatted = `${val.slice(0, 2)}-${val.slice(2, 4)}-${val.slice(4, 8)}`;
                  } else if (val.length > 2) {
                    formatted = `${val.slice(0, 2)}-${val.slice(2)}`;
                  }
                  handleChange({ target: { name: field.Field_label, value: formatted } });
                }}
                className={`premium-input
                ${fieldError ? 'border-red-500 ring-4 ring-red-500/10' : ''}`}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          )

        /* ===========================
         * DEFAULT TEXT FIELD
         * =========================== */
        default:
          return (
            <input
              {...commonProps}
              type='text'
              autoComplete='off'
              placeholder={`Enter ${field.Field_label.toLowerCase()}`}
              className={`premium-input
              ${fieldError ? 'border-red-500 ring-4 ring-red-500/10' : ''}`}
            />
          )
      }
    }

    return (
      <div
        key={index}
        className={`group transition-all duration-300 ${field.Input_type === 'textarea' ? 'md:col-span-2' : ''}`}
      >
        {field.Input_type !== 'checkbox' && (
          <label
            htmlFor={field.Field_label}
            className='premium-label group-focus-within:text-blue-600'
          >
            {field.Field_label}
            {isRequired && <span className='text-red-500 ml-1'>*</span>}
          </label>
        )}

        {renderByType()}

        {fieldError && (
          <span className='block text-red-500 text-sm mt-2 animate-pulse'>
            {fieldError}
          </span>
        )}
      </div>
    )
  },
  (prev, next) =>
    prev.fieldValue === next.fieldValue &&
    prev.fieldError === next.fieldError &&
    prev.licenseValue === next.licenseValue &&
    prev.licenseError === next.licenseError &&
    prev.uploadedFiles[prev.field.Field_label] ===
    next.uploadedFiles[next.field.Field_label] &&
    prev.fieldErrors[prev.field.Field_label] ===
    next.fieldErrors[next.field.Field_label]
)

DynamicFormField.displayName = 'DynamicFormField'
/**
 * MAIN APPLICATION FORM COMPONENT
 */
const ApplicationForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const reuploadApplicationId = searchParams.get('applicationId')
  const reuploadDataId = searchParams.get('dataId')
  const isReuploadMode = Boolean(reuploadApplicationId && reuploadDataId)

  /** STATE MANAGEMENT **/
  const [formFields, setFormFields] = useState([])
  const [formData, setFormData] = useState({})
  const [vehicleOptions, setVehicleOptions] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [uploadedFiles, setUploadedFiles] = useState({})
  const [currentPhase, setCurrentPhase] = useState(1)
  const [nonFileFields, setNonFileFields] = useState([])
  const [fileFields, setFileFields] = useState([])
  const [reuploadContext, setReuploadContext] = useState(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [submittedApplication, setSubmittedApplication] = useState(null)

  /**
   * SAFE EXTRACTOR FOR API RESPONSE ARRAYS
   */
  const extractArrayFromResponse = (
    data,
    possibleKeys = ['data', 'fields', 'formFields', 'vehicles']
  ) => {
    if (Array.isArray(data)) return data

    for (const key of possibleKeys) {
      if (data[key] && Array.isArray(data[key])) {
        return data[key]
      }
    }

    return []
  }

  /**
   * VALIDATE INDIVIDUAL FIELD
   */
  const validateField = (field, value) => {
    // Required checks
    if (field.Is_required === 1) {
      if (field.Input_type === 'checkbox') {
        if (!value) return `${field.Field_label} must be checked`
      } else if (field.Input_type === 'file') {
        if (!uploadedFiles[field.Field_label])
          return `${field.Field_label} is required`
      } else {
        if (!value || value.trim() === '') return `${field.Field_label} is required`
      }
    }

    // Email format
    if (field.Input_type === 'email') {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        return 'Please enter a valid email address'
    }

    // Number
    if (field.Input_type === 'number') {
      if (value && isNaN(value)) return 'Please enter a valid number'
    }

    // Mobile field
    if (field.Input_type === 'tel') {
      if (value && !/^\d{10}$/.test(value.replace(/\D/g, '')))
        return 'Please enter a valid phone number'
    }

    return null
  }

  /**
   * GET OPTIONS FOR SELECT FIELDS
   */
  const getFieldOptions = field => {
    const fieldLabel = field.Field_label.toLowerCase()

    if (
      fieldLabel.includes('license_type') ||
      fieldLabel.includes('license type') ||
      fieldLabel.includes('vehicle')
    ) {
      return vehicleOptions
    }

    return []
  }

  /**
   * RENDER SELECT OPTIONS
   */
  const renderFieldOptions = field => {
    const options = getFieldOptions(field)
    const fieldLabel = field.Field_label.toLowerCase()

    if (
      fieldLabel.includes('license_type') ||
      fieldLabel.includes('license type') ||
      fieldLabel.includes('vehicle')
    ) {
      return options.map(vehicle => (
        <option
          key={vehicle.Id || vehicle.id}
          value={vehicle.code || vehicle.Id || vehicle.id}
        >
          {vehicle.License_Type || vehicle.name || vehicle.type}
        </option>
      ))
    }

    return [
      <option key='no-options' value=''>
        No options available
      </option>
    ]
  }

  /**
   * FETCH VEHICLE OPTIONS
   */
  useEffect(() => {
    const fetchVehicleOptions = async () => {
      try {
        const vehicleData = await getvehicle()
        const vehicleArray = extractArrayFromResponse(vehicleData, ['data', 'vehicles'])
        setVehicleOptions(vehicleArray)
      } catch (err) {
        setError('Failed to load vehicle options')
      }
    }

    fetchVehicleOptions()
  }, [])

  /**
   * FETCH FORM FIELDS
   */
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const data = await getFormFields(id)
        const fieldsArray = [...extractArrayFromResponse(data)].sort((a, b) => {
          const priorityA = Number.isFinite(Number(a.Priority)) ? Number(a.Priority) : 0
          const priorityB = Number.isFinite(Number(b.Priority)) ? Number(b.Priority) : 0
          return priorityA - priorityB || (Number(a.Id) || 0) - (Number(b.Id) || 0)
        })

        if (fieldsArray.length === 0) {
          setError('No form fields found for this service')
          setLoading(false)
          return
        }

        setFormFields(fieldsArray)

        const nonFileFieldsList = fieldsArray.filter(f => f.Input_type !== 'file')
        const fileFieldsList = fieldsArray.filter(f => f.Input_type === 'file')

        setNonFileFields(nonFileFieldsList)
        setFileFields(fileFieldsList)

        const initialData = {}
        const initialFiles = {}

        fieldsArray.forEach(field => {
          if (field.Input_type === 'checkbox') {
            initialData[field.Field_label] = false
          } else if (field.Input_type === 'file') {
            initialFiles[field.Field_label] = null
          } else {
            initialData[field.Field_label] = ''
          }
        })

        setFormData(initialData)
        setUploadedFiles(initialFiles)

        if (isReuploadMode) {
          const token = localStorage.getItem('token')
          const storedUser = localStorage.getItem('user')
          const hasUsableToken = isTokenUsable(token)
          let user = null

          if (storedUser) {
            try {
              user = JSON.parse(storedUser)
            } catch (parseError) {
              console.error('Failed to parse stored user data:', parseError)
            }
          }

          if (!hasUsableToken && !user?.userId) {
            navigate('/')
            return
          }

          let details

          if (hasUsableToken) {
            try {
              details = await getApplicationDetails(reuploadApplicationId, {
                silent: true
              })
            } catch (primaryError) {
              if (!user?.userId) {
                throw primaryError
              }

              details = await getApplicationDetails(reuploadApplicationId, {
                userId: user.userId,
                skipAuth: true,
                silent: true
              })
            }
          } else {
            details = await getApplicationDetails(reuploadApplicationId, {
              userId: user.userId,
              skipAuth: true,
              silent: true
            })
          }

          const targetField = details?.formData?.find(
            field =>
              String(field.DataId) === String(reuploadDataId) &&
              (field.InputType === 'file' || field.Input_type === 'file')
          )

          if (!targetField) {
            setError('The requested document could not be found for reupload.')
            setLoading(false)
            return
          }

          setReuploadContext({
            applicationId: reuploadApplicationId,
            dataId: reuploadDataId,
            formFieldId: targetField.FormFieldId,
            fieldLabel: targetField.FieldLabel || targetField.Field_label
          })
          setCurrentPhase(2)
          setTermsAccepted(true)
        }

        setLoading(false)
      } catch (err) {
        setError('Failed to load form fields')
        setLoading(false)
      }
    }

    if (id) fetchFields()
    else {
      setError('Invalid service ID')
      setLoading(false)
    }
  }, [id, isReuploadMode, navigate, reuploadApplicationId, reuploadDataId])

  const visibleFileFields = reuploadContext
    ? fileFields.filter(field => String(field.Id) === String(reuploadContext.formFieldId))
    : fileFields

  const reuploadField = reuploadContext
    ? visibleFileFields[0] || {
      Id: reuploadContext.formFieldId || reuploadContext.dataId,
      Field_label: reuploadContext.fieldLabel,
      Input_type: 'file',
      Is_required: 1
    }
    : null

  /**
   * MAIN CHANGE HANDLER (STABLE)
   */
  const handleChange = useCallback(
    e => {
      const { name, value, type, checked, files } = e.target

      if (type === 'checkbox') {
        setFormData(prev => ({ ...prev, [name]: checked }))
      } else if (type === 'file') {
        if (files && files.length > 0) {
          setUploadedFiles(prev => ({ ...prev, [name]: files[0] }))
        } else {
          setUploadedFiles(prev => ({ ...prev, [name]: null }))
        }
      } else {
        setFormData(prev => ({ ...prev, [name]: value }))
      }

      if (fieldErrors[name]) {
        setFieldErrors(prev => ({ ...prev, [name]: null }))
      }

      if (error) setError('')
    },
    [fieldErrors, error]
  )

  /** STABLE LICENSE CHANGE **/
  const handleLicenseChange = useCallback(
    e => {
      handleChange({
        target: {
          name: e.target.name,
          value: e.target.value,
          type: 'text'
        }
      })
    },
    [handleChange]
  )

  /** DRAG EVENTS **/
  const handleDragOver = useCallback(e => e.preventDefault(), [])
  const handleDragLeave = useCallback(() => { }, [])
  const handleDrop = useCallback(
    (e, field) => {
      e.preventDefault()
      const { files } = e.dataTransfer

      if (files && files.length > 0) {
        const name = field.Field_label
        setUploadedFiles(prev => ({ ...prev, [name]: files[0] }))

        if (fieldErrors[name]) {
          setFieldErrors(prev => ({ ...prev, [name]: null }))
        }
      }

      if (error) setError('')
    },
    [fieldErrors, error]
  )

  /** FILE INPUT **/
  const handleFileChange = useCallback(
    (e, field) => {
      const { name, files } = e.target

      if (files && files.length > 0) {
        setUploadedFiles(prev => ({ ...prev, [name]: files[0] }))

        if (fieldErrors[name]) {
          setFieldErrors(prev => ({ ...prev, [name]: null }))
        }
      } else {
        setUploadedFiles(prev => ({ ...prev, [name]: null }))
      }

      if (error) setError('')
    },
    [fieldErrors, error]
  )
  /**
   * VALIDATE PHASE 1 (NON-FILE FIELDS)
   */
  const validatePhase1 = useCallback(() => {
    const errors = {}
    const serviceId = id === '1' || parseInt(id) === 1

    nonFileFields.forEach(field => {
      const value = formData[field.Field_label]
      const fieldError = validateField(field, value)

      if (fieldError) {
        errors[field.Field_label] = fieldError
      }

      // Special: If checkbox is checked → license input is required
      if (
        serviceId &&
        field.Input_type === 'checkbox' &&
        formData[field.Field_label] === true
      ) {
        const licenseKey = `checkbox_${field.Id}_license_number`
        const licenseValue = formData[licenseKey]

        if (!licenseValue || licenseValue.trim() === '') {
          errors[licenseKey] =
            'License number is required when this option is selected'
        }
      }
    })

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setError('Please fix the errors before proceeding')
      return false
    }

    return true
  }, [id, nonFileFields, formData])

  /** PHASE NAVIGATION **/
  const handleNextPhase = () => {
    if (validatePhase1()) {
      setFieldErrors({})
      setError('')
      setCurrentPhase(2)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePageBack = () => {
    if (currentPhase === 2 && !reuploadContext) {
      setCurrentPhase(1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    if (reuploadContext) {
      navigate(`/application-details/${reuploadContext.applicationId}`)
      return
    }

    navigate(-1)
  }

  const handlePreviousPhase = () => {
    if (reuploadContext) {
      navigate(`/application-details/${reuploadContext.applicationId}`)
      return
    }

    setCurrentPhase(1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    setSubmittedApplication(null)
    navigate('/')
  }

  const handlePrintSuccess = () => {
    window.print()
  }

  const handleDownloadPdf = () => {
    // Uses the browser's print dialog ("Save as PDF") since it reuses
    // the same print-only markup as the receipt.
    window.print()
  }

  const findFieldValue = (data, keywords) => {
    if (!data) return ''
    const entry = Object.entries(data).find(([key]) =>
      keywords.some(k => key.toLowerCase().includes(k))
    )
    return entry && entry[1] ? String(entry[1]) : ''
  }

  const buildSubmissionSummary = data => {
    if (!data || typeof data !== 'object') return []

    return Object.entries(data)
      .filter(([key, value]) => {
        if (['serviceId', 'totalFields', 'timestamp'].includes(key)) return false
        if (value === null || value === undefined) return false
        if (typeof value === 'string' && value.trim() === '') return false
        if (typeof value === 'boolean' && value === false) return false
        return true
      })
      .map(([key, value]) => {
        let displayValue = String(value)

        if (value === 'file_uploaded') {
          displayValue = 'Uploaded'
        } else if (typeof value === 'boolean') {
          displayValue = value ? 'Yes' : 'No'
        }

        return {
          label: key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase()),
          value: displayValue
        }
      })
  }

  /**
   * SUBMIT FORM
   */
  const handleSubmit = async e => {
    e.preventDefault()
    setSubmitting(true)
    setError('')
    setFieldErrors({})

    try {
      if (reuploadContext) {
        const targetField = reuploadField

        if (!targetField || !uploadedFiles[targetField.Field_label]) {
          setFieldErrors({
            [reuploadContext.fieldLabel]: `${reuploadContext.fieldLabel} is required`
          })
          setError('Please upload the requested document before continuing.')
          setSubmitting(false)
          return
        }

        const token = localStorage.getItem('token')

        const formDataToSend = new FormData()
        formDataToSend.append('document', uploadedFiles[targetField.Field_label])

        const response = await reuploadApplicationDocument(
          reuploadContext.applicationId,
          reuploadContext.dataId,
          formDataToSend,
          token
        )

        if (response.success) {
          alert('Document reuploaded successfully!')
          navigate(`/application-details/${reuploadContext.applicationId}`)
          return
        }

        setError('Reupload failed, please try again.')
        setSubmitting(false)
        return
      }

      const errors = {}
      const serviceId = id === '1' || parseInt(id) === 1

      /** VALIDATE ALL FIELDS **/
      formFields.forEach(field => {
        let fieldError = null

        if (field.Input_type === 'file') {
          if (!uploadedFiles[field.Field_label] && field.Is_required === 1) {
            errors[field.Field_label] = `${field.Field_label} is required`
          }
        } else {
          fieldError = validateField(field, formData[field.Field_label])
        }

        if (fieldError) {
          errors[field.Field_label] = fieldError
        }

        // Again: serviceId special case
        if (
          serviceId &&
          field.Input_type === 'checkbox' &&
          formData[field.Field_label] === true
        ) {
          const licenseKey = `checkbox_${field.Id}_license_number`
          const licenseValue = formData[licenseKey]

          if (!licenseValue || licenseValue.trim() === '') {
            errors[licenseKey] = 'License number is required'
          }
        }
      })

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors)
        setError('Please fix the fields below')
        setSubmitting(false)
        return
      }

      /** PREPARE FINAL DATA **/
      const token = localStorage.getItem('token')

      const regularFormData = {}
      const fileFieldsList = []
      const fieldValues = []

      formFields.forEach(field => {
        if (field.Input_type === 'file') {
          if (uploadedFiles[field.Field_label]) {
            fileFieldsList.push({
              fieldId: field.Id,
              fieldLabel: field.Field_label,
              file: uploadedFiles[field.Field_label]
            })
            regularFormData[field.Field_label] = 'file_uploaded'
            fieldValues.push({
              fieldId: field.Id,
              fieldLabel: field.Field_label,
              value: 'file_uploaded'
            })
          } else {
            regularFormData[field.Field_label] = ''
            fieldValues.push({
              fieldId: field.Id,
              fieldLabel: field.Field_label,
              value: ''
            })
          }
        } else {
          const val = formData[field.Field_label]
          const normalizedValue = val !== undefined && val !== null ? String(val).trim() : ''
          regularFormData[field.Field_label] = normalizedValue
          regularFormData[`${field.Field_label}_id`] = field.Id
          fieldValues.push({
            fieldId: field.Id,
            fieldLabel: field.Field_label,
            value: normalizedValue
          })
        }
      })

      regularFormData.serviceId = id
      regularFormData.totalFields = formFields.length
      regularFormData.timestamp = new Date().toISOString()

      /** CONSTRUCT FORM DATA SUBMISSION */
      const formDataToSend = new FormData()
      formDataToSend.append('formData', JSON.stringify(regularFormData))
      formDataToSend.append('fieldValues', JSON.stringify(fieldValues))
      formDataToSend.append('serviceId', id)

      fileFieldsList.forEach(item => {
        formDataToSend.append(`file_${item.fieldId}`, item.file, item.file.name)
        formDataToSend.append(`file_${item.fieldId}_label`, item.fieldLabel)
      })

      /** API CALL **/
      const response = await submitApplication(id, formDataToSend)

      if (response.success) {
        const submissionSummary = buildSubmissionSummary(regularFormData)

        setSubmittedApplication({
          applicationNumber: response.applicationNumber || `APP-${response.applicationId || 'N/A'}`,
          message: response.message || 'Your application has been submitted successfully.',
          submittedAt: new Date().toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short'
          }),
          summaryFields: submissionSummary,
          name: findFieldValue(regularFormData, ['name']) || '—',
          mobile: findFieldValue(regularFormData, ['mobile', 'phone']) || '—',
          serviceType:
            [response.ServiceName, response.ServiceType, response.ServiceCategory]
              .filter(Boolean)
              .join(' • ') ||
            location.state?.serviceName ||
            '—'
        })
        setShowSuccessModal(true)
        setError('')
      } else {
        setError('Submission failed, please try again.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * FORM VALIDATION (BUTTON ENABLE/DISABLE)
   */
  const isFormValid = useCallback(() => {
    if (reuploadContext) {
      return Boolean(uploadedFiles[reuploadContext.fieldLabel])
    }

    if (currentPhase === 1) {
      const requiredFieldsFilled = nonFileFields.every(field => {
        if (field.Is_required === 1) {
          if (field.Input_type === 'checkbox') {
            return formData[field.Field_label] === true
          } else {
            return formData[field.Field_label]?.trim?.() !== ''
          }
        }
        return true
      })

      const noErrors =
        Object.keys(fieldErrors).length === 0 ||
        Object.values(fieldErrors).every(err => !err)

      return requiredFieldsFilled && noErrors
    }

    // Phase 2 validation
    const requiredFilesUploaded = fileFields.every(field => {
      if (field.Is_required === 1) {
        return uploadedFiles[field.Field_label]
      }
      return true
    })

    return requiredFilesUploaded && termsAccepted
  }, [
    currentPhase,
    nonFileFields,
    fileFields,
    formData,
    fieldErrors,
    uploadedFiles,
    termsAccepted,
    reuploadContext,
    reuploadField
  ])
  /**
   * LOADING SCREEN
   */
  if (loading) {
    return (
      <div className='flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900'>
        <div className='flex-1 flex items-center justify-center p-6'>
          <div className='text-center text-gray-600 dark:text-gray-400'>
            Loading form fields...
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  /**
   * ERROR SCREEN BEFORE FORM LOAD
   */
  if (error && !submitting && formFields.length === 0) {
    return (
      <div className='flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900'>
        <div className='flex-1 flex items-center justify-center p-6'>
          <div className='text-center'>
            <p className='text-red-500 mb-4'>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className='px-6 py-3 bg-blue-500 text-white rounded hover:bg-blue-600'
            >
              Retry
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  /**
   * MAIN FORM UI
   */
  return (
    <div className='flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900'>

      {/* Hides the native up/down spinner arrows on all type="number" inputs
          rendered by DynamicFormField, since fields are generated dynamically
          and we can't target them individually. */}
      <style>{`
        input.no-spinner::-webkit-outer-spin-button,
        input.no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spinner[type='number'] {
          -moz-appearance: textfield;
        }
      `}</style>

      {/* BACK BUTTON */}
      <div className='p-4'>
        <BackButton onBack={handlePageBack} />
      </div>

      <style>{`
        @media print {
          body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div className='flex-1 flex items-center justify-center p-4 sm:p-6'>
        <div className='w-full max-w-4xl bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 sm:p-6'>

          {/* PHASE INDICATOR */}
          <div className='mb-6'>
            <div className='flex items-center justify-between'>

              {/* PHASE 1 */}
              <div className='flex items-center gap-2'>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentPhase === 1
                    ? 'bg-blue-500 text-white'
                    : 'bg-green-500 text-white'
                    }`}
                >
                  {currentPhase === 1 ? '1' : '✓'}
                </div>
                <div>
                  <div className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Phase 1
                  </div>
                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                    Basic Information
                  </div>
                </div>
              </div>

              {/* LINE */}
              <div className='flex-1 h-1 mx-3 bg-gray-300 dark:bg-gray-600'>
                <div
                  className={`h-full transition-all duration-300 ${currentPhase === 2 ? 'bg-blue-500 w-full' : 'w-0'
                    }`}
                />
              </div>

              {/* PHASE 2 */}
              <div className='flex items-center gap-2'>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${currentPhase === 2
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                    }`}
                >
                  2
                </div>
                <div>
                  <div className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Phase 2
                  </div>
                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                    Document Uploads
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* HEADER */}
          <div className='flex items-center gap-3 mb-6'>
            <FileText className='text-blue-500' size={28} />
            <div>
              <h1 className='text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100'>
                {reuploadContext
                  ? `Reupload ${reuploadContext.fieldLabel}`
                  : currentPhase === 1
                    ? 'Application Form - Basic Info (v2)'
                    : 'Application Form - Document Uploads (v2)'}
              </h1>
              <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                {reuploadContext
                  ? 'Upload the requested document to update this application.'
                  : currentPhase === 1
                    ? 'Fill all required fields marked with *'
                    : 'Upload all required documents marked with *'}
              </p>
            </div>
          </div>

          {reuploadContext && (
            <div className='mb-6 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800'>
              This upload will replace only the requested document for application #{reuploadContext.applicationId}.
            </div>
          )}

          {/* GENERAL ERROR */}
          {error && (
            <div className='mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800'>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* FORM */}
          <form onSubmit={handleSubmit} className='space-y-6'>

            {/* ============================
             * PHASE 1 — NON-FILE FIELDS
             * ============================ */}
            {currentPhase === 1 && (
              <>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {nonFileFields.map((field, index) => (
                    <DynamicFormField
                      key={field.Id}
                      field={field}
                      index={index}
                      fieldValue={formData[field.Field_label]}
                      fieldError={fieldErrors[field.Field_label]}
                      licenseValue={formData[`checkbox_${field.Id}_license_number`]}
                      licenseError={fieldErrors[`checkbox_${field.Id}_license_number`]}
                      handleChange={handleChange}
                      handleLicenseChange={handleLicenseChange}
                      handleDragOver={handleDragOver}
                      handleDragLeave={handleDragLeave}
                      handleDrop={handleDrop}
                      handleFileChange={handleFileChange}
                      uploadedFiles={uploadedFiles}
                      vehicleOptions={vehicleOptions}
                      renderFieldOptions={renderFieldOptions}
                      id={id}
                      setUploadedFiles={setUploadedFiles}
                      setFieldErrors={setFieldErrors}
                      fieldErrors={fieldErrors}
                    />
                  ))}
                </div>

                {/* NEXT BUTTON */}
                <div className='flex justify-end mt-6'>
                  <button
                    type='button'
                    onClick={handleNextPhase}
                    disabled={!isFormValid()}
                    className={`px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                    ${!isFormValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Next: Upload Documents →
                  </button>
                </div>
              </>
            )}

            {/* ============================
             * PHASE 2 — FILE UPLOADS
             * ============================ */}
            {currentPhase === 2 && (
              <>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  {(reuploadContext ? [reuploadField].filter(Boolean) : visibleFileFields).map((field, index) => (
                    <DynamicFormField
                      key={field.Id}
                      field={field}
                      index={index}
                      fieldValue={formData[field.Field_label]}
                      fieldError={fieldErrors[field.Field_label]}
                      handleChange={handleChange}
                      handleFileChange={handleFileChange}
                      uploadedFiles={uploadedFiles}
                      vehicleOptions={vehicleOptions}
                      renderFieldOptions={renderFieldOptions}
                      id={id}
                      handleDragOver={handleDragOver}
                      handleDragLeave={handleDragLeave}
                      handleDrop={handleDrop}
                      setUploadedFiles={setUploadedFiles}
                      setFieldErrors={setFieldErrors}
                      fieldErrors={fieldErrors}
                    />
                  ))}
                </div>

                {/* TERMS & CONDITIONS */}
                {!reuploadContext && (
                  <div className='mt-6'>
                    <label className='flex items-start gap-3'>
                      <input
                        type='checkbox'
                        checked={termsAccepted}
                        onChange={e => setTermsAccepted(e.target.checked)}
                        className='w-5 h-5 accent-blue-500'
                        required
                      />
                      <span className='text-sm text-gray-700 dark:text-gray-300'>
                        I confirm that all information provided is accurate.
                        <span className='text-red-500 ml-1'>*</span>
                      </span>
                    </label>
                  </div>
                )}

                {/* ACTION BUTTONS */}
                <div className='flex flex-col sm:flex-row justify-between mt-6 gap-4'>

                  {/* BACK BUTTON */}
                  <button
                    type='button'
                    onClick={handlePreviousPhase}
                    className='px-8 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg'
                  >
                    {reuploadContext ? 'Back to Application Details' : 'Back to Information'}
                  </button>

                  {/* SUBMIT BUTTON */}
                  <button
                    type='submit'
                    disabled={!isFormValid() || submitting}
                    className={`px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg
                    ${!isFormValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {submitting
                      ? (reuploadContext ? 'Reuploading...' : 'Submitting...')
                      : (reuploadContext ? 'Reupload Document' : 'Submit Application')}
                  </button>
                </div>
              </>
            )}

          </form>
        </div>
      </div>

      {showSuccessModal && submittedApplication && (
        <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm'>
          <style>{`
            @media print {
              body * { visibility: hidden; }
              .print-success-card, .print-success-card * { visibility: visible; }
              .print-success-card { position: absolute; left: 0; top: 0; width: 100%; }
              .no-print { display: none !important; }
            }
          `}</style>

          <div className='print-success-card relative w-full max-w-md overflow-hidden rounded-[40px] shadow-[0_25px_90px_rgba(15,23,42,0.35)]'>

            {/* Close button */}
            <button
              type='button'
              onClick={handleSuccessModalClose}
              className='no-print absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-900 shadow-md transition hover:bg-slate-100'
            >
              <X size={20} />
            </button>

            {/* Top panel: QR + application number */}
            <div className='relative overflow-hidden bg-gradient-to-br from-sky-100 via-blue-100 to-blue-200 px-8 pb-8 pt-12'>
              {/* Decorative sparkles */}
              <div className='pointer-events-none absolute inset-0 overflow-hidden'>
                {[
                  { top: '6%', left: '9%', size: 16, color: 'text-blue-400' },
                  { top: '17%', left: '25%', size: 8, color: 'text-lime-500' },
                  { top: '30%', left: '13%', size: 11, color: 'text-white' },
                  { top: '8%', right: '10%', size: 18, color: 'text-blue-500' },
                  { top: '20%', right: '7%', size: 9, color: 'text-lime-500' },
                  { top: '32%', right: '13%', size: 12, color: 'text-white' }
                ].map((s, i) => (
                  <span
                    key={i}
                    className={`absolute ${s.color} opacity-90`}
                    style={{ top: s.top, left: s.left, right: s.right }}
                  >
                    <Sparkle size={s.size} fill='currentColor' />
                  </span>
                ))}
              </div>

              {/* QR circle */}
              <div className='relative mx-auto flex h-48 w-48 flex-col items-center justify-center rounded-full border-[3px] border-slate-900 bg-lime-200 shadow-inner'>
                <div className='flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg bg-white p-1'>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=0&data=${encodeURIComponent(
                      `${window.location.origin}/my-applications`
                    )}`}
                    alt='Tracking QR'
                    className='h-full w-full object-contain'
                  />
                </div>
                <p className='mt-2 text-xs font-extrabold tracking-wide text-slate-900'>TRACKING QR</p>
              </div>

              {/* Application number pill */}
              <div className='relative mx-auto mt-6 w-full max-w-[340px] rounded-full border-[3px] border-slate-900 bg-lime-200 px-8 py-3 text-center'>
                <p className='flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-slate-900'>
                  <span className='h-px w-4 bg-slate-900/60' />
                  Application Number
                  <span className='h-px w-4 bg-slate-900/60' />
                </p>
                <p className='mt-1 text-2xl font-black tracking-wide text-slate-900'>
                  {submittedApplication.applicationNumber}
                </p>
              </div>
            </div>

            {/* Bottom panel: details + actions */}
            <div className='bg-blue-100 px-8 pb-6 pt-6'>
              <div className='rounded-3xl bg-white p-5 shadow-sm'>
                {[
                  { icon: User, label: 'Name', value: submittedApplication.name },
                  { icon: ClipboardList, label: 'Service Type', value: submittedApplication.serviceType },
                  { icon: Phone, label: 'Mobile No.', value: submittedApplication.mobile },
                  { icon: Calendar, label: 'Date & Time', value: submittedApplication.submittedAt },
                  { icon: FileText, label: 'Status', value: 'SUBMITTED' }
                ].map((row, i, arr) => (
                  <div
                    key={row.label}
                    className={`flex items-center justify-between py-3 ${
                      i !== arr.length - 1 ? 'border-b border-dashed border-slate-200' : ''
                    }`}
                  >
                    <div className='flex items-center gap-3'>
                      <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white'>
                        <row.icon size={15} />
                      </span>
                      <span className='text-sm text-slate-500'>{row.label}</span>
                    </div>

                    {row.label === 'Status' ? (
                      <span className='inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-bold tracking-wide text-emerald-600'>
                        <CheckCircle2 size={14} />
                        SUBMITTED
                      </span>
                    ) : (
                      <span className='text-sm font-bold text-slate-900'>{row.value || '—'}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div className='no-print mt-5 grid grid-cols-2 gap-3'>
                <button
                  type='button'
                  onClick={handlePrintSuccess}
                  className='flex items-center justify-center gap-2 rounded-2xl bg-blue-600 py-3 text-sm font-semibold text-white transition hover:bg-blue-700'
                >
                  <Printer size={16} />
                  Print Receipt
                </button>
                <button
                  type='button'
                  onClick={handleDownloadPdf}
                  className='flex items-center justify-center gap-2 rounded-2xl border-2 border-blue-600 bg-white py-3 text-sm font-semibold text-blue-600 transition hover:bg-blue-50'
                >
                  <Download size={16} />
                  Download PDF
                </button>
              </div>

              <button
                type='button'
                onClick={handleSuccessModalClose}
                className='no-print mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-50'
              >
                <Home size={16} />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}

export default ApplicationForm